import React, { useState, useEffect } from "react";
import {
  Box,
  Checkbox,
  Chip,
  IconButton,
  Paper,
  Slider,
  Stack,
  Typography,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import color from "../../Constants/colors";
import {
  useSingleComoAgent,
  useCreateComoAgent,
  useUpdateComoAgent,
  useComoTools,
  useMcpConnections,
} from "../../Query/useComoAgent";
import { useSkills } from "../../Query/useSkill";
import { useKnowledgeList } from "../../Query/useKnowledge";
import { ComoAgentCreatePayload, AgentMcpConfig } from "../../Models/comoAgent";
import Loading from "../Common/Loading";
import { useWorkspaceColors } from "../../Contexts/WorkspaceColorContext";
import { useLLMModels } from "../../Query/useLLM";
import { getModelIcon } from "../../Helpers/modelHelpers";
import { ComoButton } from "../Common/ComoButton";
import { ComoInput } from "../Common/ComoInput";
import { ComoSelect, ComoSelectOption } from "../Common/ComoSelect";
import { useTranslation } from "react-i18next";

const ComoAgentForm = () => {
  const { agentId } = useParams<{ agentId: string }>();
  const isEdit = !!agentId;
  const navigate = useNavigate();
  const { colors: workspace } = useWorkspaceColors();
  const { t } = useTranslation("comoAgentForm");

  const { data: existingAgent, isLoading: isAgentLoading } = useSingleComoAgent(
    Number(agentId) || 0,
  );
  const { data: tools = [] } = useComoTools();
  const { data: skills = [] } = useSkills();
  const { data: knowledgeBases = [] } = useKnowledgeList();
  const { data: llmData } = useLLMModels();
  const { data: mcpConnections = [] } = useMcpConnections();
  const createMutation = useCreateComoAgent();
  const updateMutation = useUpdateComoAgent();

  const [name, setName] = useState("");
  const [systemPrompt, setSystemPrompt] = useState("");
  const [temperature, setTemperature] = useState(0.7);
  const [maxIterations, setMaxIterations] = useState(5);
  const [exampleMessages, setExampleMessages] = useState<string[]>([]);
  const [newExampleMessage, setNewExampleMessage] = useState("");
  const [defaultModel, setDefaultModel] = useState<number | null>(null);
  const [selectedTools, setSelectedTools] = useState<string[]>([]);
  const [selectedSkills, setSelectedSkills] = useState<number[]>([]);
  const [selectedKnowledgeBases, setSelectedKnowledgeBases] = useState<
    number[]
  >([]);
  const [selectedMcpConfigs, setSelectedMcpConfigs] = useState<
    AgentMcpConfig[]
  >([]);

  const defaultModelOptions: ComoSelectOption[] = [
    { id: "", label: t("none") },
    ...(llmData?.all_models || []).map(
      (model: { model_id: number; model_name: string }) => ({
        id: model.model_id,
        label: model.model_name,
        iconSrc: getModelIcon(model.model_name),
      }),
    ),
  ];

  const selectedDefaultModelOption =
    defaultModelOptions.find((option) => option.id === (defaultModel ?? "")) ||
    defaultModelOptions[0];

  const toolOptions: ComoSelectOption[] = tools.map((tool) => ({
    id: tool.id,
    label: tool.name,
    subtitle: tool.description,
  }));

  const selectedToolOptions = toolOptions.filter((option) =>
    selectedTools.includes(String(option.id)),
  );

  const skillOptions: ComoSelectOption[] = skills.map((skill) => ({
    id: skill.id,
    label: skill.name,
    subtitle: skill.description,
  }));

  const selectedSkillOptions = skillOptions.filter((option) =>
    selectedSkills.includes(Number(option.id)),
  );

  const knowledgeBaseOptions: ComoSelectOption[] = (
    knowledgeBases as Array<{ id: number; name: string }>
  ).map((kb) => ({
    id: kb.id,
    label: kb.name,
  }));

  const selectedKnowledgeBaseOptions = knowledgeBaseOptions.filter((option) =>
    selectedKnowledgeBases.includes(Number(option.id)),
  );

  useEffect(() => {
    if (existingAgent && isEdit) {
      setName(existingAgent.name);
      setSystemPrompt(existingAgent.system_prompt);
      setTemperature(existingAgent.temperature);
      setMaxIterations(existingAgent.max_iterations ?? 5);
      setExampleMessages(existingAgent.example_messages || []);
      setDefaultModel(existingAgent.default_model);
      setSelectedTools(existingAgent.tools || []);
      setSelectedSkills(existingAgent.skills || []);
      setSelectedKnowledgeBases(existingAgent.knowledge_base_ids || []);
      setSelectedMcpConfigs(
        (existingAgent.mcp_connections || []).map((mc) => ({
          mcp_connection_id: mc.id,
          enabled_tools: mc.enabled_tools || [],
        })),
      );
    }
  }, [existingAgent, isEdit]);

  const handleSubmit = () => {
    const payload: ComoAgentCreatePayload = {
      name,
      system_prompt: systemPrompt,
      temperature,
      max_iterations: maxIterations,
      example_messages: exampleMessages,
      default_model: defaultModel,
      tools: selectedTools,
      skills: selectedSkills,
      knowledge_base_ids: selectedKnowledgeBases,
      mcp_connections: selectedMcpConfigs,
    };

    if (isEdit) {
      updateMutation.mutate(
        { agentId: Number(agentId), data: payload },
        { onSuccess: () => navigate("/como_agents") },
      );
    } else {
      createMutation.mutate(payload, {
        onSuccess: () => navigate("/como_agents"),
      });
    }
  };

  const handleAddExampleMessage = () => {
    if (newExampleMessage.trim()) {
      setExampleMessages([...exampleMessages, newExampleMessage.trim()]);
      setNewExampleMessage("");
    }
  };

  const handleRemoveExampleMessage = (index: number) => {
    setExampleMessages(exampleMessages.filter((_, i) => i !== index));
  };

  const isMcpSelected = (connId: number) =>
    selectedMcpConfigs.some((c) => c.mcp_connection_id === connId);

  const toggleMcpConnection = (connId: number) => {
    if (isMcpSelected(connId)) {
      setSelectedMcpConfigs((prev) =>
        prev.filter((c) => c.mcp_connection_id !== connId),
      );
    } else {
      const conn = mcpConnections.find((c) => c.id === connId);
      const allToolNames = (conn?.cached_tools || []).map(
        (t: { name: string }) => t.name,
      );
      setSelectedMcpConfigs((prev) => [
        ...prev,
        { mcp_connection_id: connId, enabled_tools: allToolNames },
      ]);
    }
  };

  const toggleMcpTool = (connId: number, toolName: string) => {
    setSelectedMcpConfigs((prev) =>
      prev.map((c) => {
        if (c.mcp_connection_id !== connId) return c;
        const enabled = c.enabled_tools.includes(toolName)
          ? c.enabled_tools.filter((t) => t !== toolName)
          : [...c.enabled_tools, toolName];
        return { ...c, enabled_tools: enabled };
      }),
    );
  };

  const getEnabledTools = (connId: number) =>
    selectedMcpConfigs.find((c) => c.mcp_connection_id === connId)
      ?.enabled_tools || [];

  if (isEdit && isAgentLoading) {
    return (
      <Loading
        backgroundColor={color.gray100}
        color1={workspace.accent}
        color2={workspace.primary_dark}
      />
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: color.gray50 }}>
      <Box sx={{ maxWidth: "1024px", mx: "auto", px: { xs: 2, sm: 3 }, py: 4 }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 3 },
            mb: 3,
            borderRadius: 2,
            border: `1px solid ${color.gray200}`,
            borderLeft: `4px solid ${workspace.primary_dark}`,
            background: color.white,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <IconButton
              onClick={() => navigate("/como_agents")}
              size="small"
              sx={{ color: workspace.primary_dark }}
            >
              <ArrowBackIcon />
            </IconButton>
            <GroupsOutlinedIcon
              sx={{ fontSize: 32, color: workspace.primary_dark }}
            />
            <Typography
              variant="h5"
              sx={{ fontWeight: 600, color: workspace.primary_dark }}
            >
              {isEdit ? t("editTeamMember") : t("createTeamMember")}{" "}
            </Typography>
          </Stack>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            borderRadius: 2,
            border: `1px solid ${color.gray200}`,
            borderLeft: `4px solid ${workspace.primary_dark}`,
            background: color.white,
            overflow: "visible",
          }}
        >
          <Box sx={{ p: 3, display: "flex", flexDirection: "column", gap: 3 }}>
            <ComoInput
              placeholder={t("name")}
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />

            <ComoInput
              placeholder={t("systemPrompt")}
              value={systemPrompt}
              onChange={(e) => setSystemPrompt(e.target.value)}
              multiline
              rows={6}
            />

            <Box>
              <Typography
                variant="body2"
                sx={{ mb: 1, fontWeight: 500, color: color.primary_dark }}
              >
                {t("temperature")}: {temperature.toFixed(2)}
              </Typography>
              <Slider
                value={temperature}
                onChange={(_, value) => setTemperature(value as number)}
                min={0}
                max={1}
                step={0.01}
                valueLabelDisplay="auto"
                sx={{
                  color: color.accent,
                  "& .MuiSlider-thumb": { bgcolor: color.accent },
                  "& .MuiSlider-track": { bgcolor: color.accent },
                  "& .MuiSlider-rail": { bgcolor: color.gray300 },
                }}
              />
            </Box>

            <Box>
              <Typography
                variant="body2"
                sx={{ mb: 1, fontWeight: 500, color: color.primary_dark }}
              >
                {t("maxToolCallIterations")}: {maxIterations}{" "}
              </Typography>
              <Slider
                value={maxIterations}
                onChange={(_, value) => setMaxIterations(value as number)}
                min={1}
                max={20}
                step={1}
                marks
                valueLabelDisplay="auto"
                sx={{
                  color: color.accent,
                  "& .MuiSlider-thumb": { bgcolor: color.accent },
                  "& .MuiSlider-track": { bgcolor: color.accent },
                  "& .MuiSlider-rail": { bgcolor: color.gray300 },
                }}
              />
            </Box>

            <Box>
              <Typography
                variant="body2"
                sx={{ mb: 1, fontWeight: 500, color: color.primary_dark }}
              >
                {t("defaultModel")}{" "}
              </Typography>
              <ComoSelect
                options={defaultModelOptions}
                selectedOption={selectedDefaultModelOption}
                onSelect={(option) =>
                  setDefaultModel(option.id === "" ? null : Number(option.id))
                }
                placeholder={t("selectDefaultModel")}
                sx={{ mb: 0, width: "100%" }}
              />
            </Box>

            <Box>
              <Typography
                variant="body2"
                sx={{ mb: 1, fontWeight: 500, color: color.primary_dark }}
              >
                {t("tools")}
              </Typography>
              <ComoSelect
                options={toolOptions}
                selectedOptions={selectedToolOptions}
                multiple
                onMultiSelect={(options) =>
                  setSelectedTools(options.map((option) => String(option.id)))
                }
                placeholder={t("selectTools")}
                sx={{ mb: 0, width: "100%" }}
              />
            </Box>

            <Box>
              <Typography
                variant="body2"
                sx={{ mb: 1, fontWeight: 500, color: color.primary_dark }}
              >
                {t("skills")}
              </Typography>
              <ComoSelect
                options={skillOptions}
                selectedOptions={selectedSkillOptions}
                multiple
                onMultiSelect={(options) =>
                  setSelectedSkills(options.map((option) => Number(option.id)))
                }
                placeholder={t("selectSkills")}
                sx={{ mb: 0, width: "100%" }}
              />
            </Box>

            <Box>
              <Typography
                variant="body2"
                sx={{ mb: 1, fontWeight: 500, color: color.primary_dark }}
              >
                {t("knowledgeBases")}
              </Typography>
              <ComoSelect
                options={knowledgeBaseOptions}
                selectedOptions={selectedKnowledgeBaseOptions}
                multiple
                onMultiSelect={(options) =>
                  setSelectedKnowledgeBases(
                    options.map((option) => Number(option.id)),
                  )
                }
                placeholder={t("selectKnowledgeBases")}
                sx={{ mb: 0, width: "100%" }}
              />
            </Box>

            {mcpConnections.length > 0 && (
              <Box>
                <Typography
                  variant="body2"
                  sx={{ mb: 1, fontWeight: 500, color: color.primary_dark }}
                >
                  {t("mcpConnections")}
                </Typography>
                {mcpConnections.map((conn) => {
                  const selected = isMcpSelected(conn.id);
                  const enabledTools = getEnabledTools(conn.id);
                  return (
                    <Paper
                      key={conn.id}
                      elevation={0}
                      sx={{
                        mb: 1,
                        p: 2,
                        border: `1px solid ${selected ? color.accent : color.gray200}`,
                        borderRadius: 2,
                        bgcolor: selected ? color.orange50 : color.white,
                      }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Checkbox
                          checked={selected}
                          onChange={() => toggleMcpConnection(conn.id)}
                          sx={{
                            p: 0,
                            color: color.accent,
                            "&.Mui-checked": { color: color.accent },
                          }}
                        />
                        <Box sx={{ flex: 1 }}>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 600, color: color.primary_dark }}
                          >
                            {conn.name}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ color: color.gray500 }}
                          >
                            {conn.url}
                          </Typography>
                        </Box>
                        <Chip
                          label={t("toolsCount", {
                            count: (conn.cached_tools || []).length,
                          })}
                          size="small"
                          sx={{
                            bgcolor: color.green50,
                            color: color.green700,
                            fontSize: "0.75rem",
                          }}
                        />
                      </Box>
                      {selected && (conn.cached_tools || []).length > 0 && (
                        <Box
                          sx={{
                            mt: 1.5,
                            ml: 4,
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 0.5,
                          }}
                        >
                          {(conn.cached_tools || []).map(
                            (tool: { name: string }) => {
                              const toolEnabled = enabledTools.includes(
                                tool.name,
                              );
                              return (
                                <Chip
                                  key={tool.name}
                                  label={tool.name}
                                  size="small"
                                  clickable
                                  onClick={() =>
                                    toggleMcpTool(conn.id, tool.name)
                                  }
                                  sx={{
                                    bgcolor: toolEnabled
                                      ? color.blue50
                                      : color.gray100,
                                    color: toolEnabled
                                      ? color.blue700
                                      : color.gray400,
                                    border: `1px solid ${toolEnabled ? color.blue200 : color.gray200}`,
                                    fontSize: "0.75rem",
                                  }}
                                />
                              );
                            },
                          )}
                        </Box>
                      )}
                    </Paper>
                  );
                })}
              </Box>
            )}

            <Box>
              <Typography
                variant="body2"
                sx={{ mb: 1, fontWeight: 500, color: color.primary_dark }}
              >
                {t("exampleMessages")}
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1 }}>
                {exampleMessages.map((msg, index) => (
                  <Chip
                    key={index}
                    label={msg}
                    onDelete={() => handleRemoveExampleMessage(index)}
                    sx={{
                      bgcolor: color.orange50,
                      color: color.primary_dark,
                      border: `1px solid ${color.orange200}`,
                      "& .MuiChip-deleteIcon": {
                        color: color.gray400,
                        "&:hover": { color: color.red },
                      },
                    }}
                  />
                ))}
              </Box>
              <Box sx={{ display: "flex", gap: 1 }}>
                <ComoInput
                  size="small"
                  value={newExampleMessage}
                  onChange={(e) => setNewExampleMessage(e.target.value)}
                  placeholder={t("addExampleMessage")}
                  fullWidth
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleAddExampleMessage();
                    }
                  }}
                />
                <ComoButton
                  variant="outlined"
                  onClick={handleAddExampleMessage}
                >
                  {t("add")}
                </ComoButton>
              </Box>
            </Box>
          </Box>

          <Box
            sx={{
              p: 3,
              display: "flex",
              gap: 2,
              justifyContent: "flex-end",
              borderTop: `1px solid ${color.gray200}`,
            }}
          >
            <ComoButton
              variant="outlined"
              onClick={() => navigate("/como_agents")}
            >
              {t("cancel")}
            </ComoButton>
            <ComoButton
              onClick={handleSubmit}
              disabled={
                !name.trim() ||
                createMutation.isPending ||
                updateMutation.isPending
              }
            >
              {isEdit ? t("update") : t("create")}
            </ComoButton>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default ComoAgentForm;
